/**
 * FIREBOX REWARDS BANNER — Client-side JavaScript
 *
 * Reads customer metafields via Shopify Liquid (injected as data attributes),
 * renders the rewards banner with safe DOM methods, and runs a live countdown.
 *
 * Data source: Shopify customer metafields (namespace: firebox_rewards)
 *   - current_points          (number_decimal)  this quarter's balance
 *   - quarter_label           (single_line_text_field)
 *   - points_cap_reached      (boolean)
 *   - previous_quarter_points (number_decimal)  last quarter's claimable balance
 *   - previous_quarter_label  (single_line_text_field)
 *   - claim_deadline          (date)            last day of the 2-week claim window
 *
 * Points come from ORDER totals (SparkLayer correct pricing), not product prices.
 *
 * CLAIM WINDOW (2026-06-10, Sarah Gibson request):
 *   During the first two weeks of a new quarter, members can still claim the
 *   PREVIOUS quarter's points. While today <= claim_deadline AND they have
 *   previous-quarter points, the banner shows BOTH balances side by side
 *   (last quarter "to claim" + this quarter "earned"). Outside the window it
 *   falls back to the single current-balance banner exactly as before.
 */

(function() {
  'use strict';

  // ===== CONFIGURATION =====
  var CONFIG = {
    POINTS_CAP: 65000,
    REWARDS_PAGE: '/pages/firebox-online-rewards',
    REGISTER_PAGE: '/account/register',
    QUARTERS: [
      { label: 'Q1', months: 'Jan — Mar', start: 0, end: 2 },
      { label: 'Q2', months: 'Apr — Jun', start: 3, end: 5 },
      { label: 'Q3', months: 'Jul — Sep', start: 6, end: 8 },
      { label: 'Q4', months: 'Oct — Dec', start: 9, end: 11 }
    ],
    // Fallback claim-window length if the claim_deadline metafield is absent:
    // first N days of the quarter (1st .. Nth inclusive). Firebox = 1st-15th.
    CLAIM_WINDOW_DAYS: 15,
    UPDATE_INTERVAL: 60000
  };

  var MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  // ===== HELPER: Create element with classes and text =====
  function el(tag, classNames, textContent) {
    var node = document.createElement(tag);
    if (classNames) node.className = classNames;
    if (textContent) node.textContent = textContent;
    return node;
  }

  // ===== QUARTER UTILITIES =====
  function getCurrentQuarter() {
    var now = new Date();
    var month = now.getMonth();
    var year = now.getFullYear();
    var q = CONFIG.QUARTERS.filter(function(q) {
      return month >= q.start && month <= q.end;
    })[0];
    return {
      label: q.label,
      months: q.months,
      year: year,
      display: q.label + ' ' + year
    };
  }

  function getQuarterEndDate() {
    var now = new Date();
    var month = now.getMonth();
    var year = now.getFullYear();
    var q = CONFIG.QUARTERS.filter(function(q) {
      return month >= q.start && month <= q.end;
    })[0];
    var endMonth = q.end + 1;
    if (endMonth > 11) {
      return new Date(year + 1, 0, 1, 0, 0, 0);
    }
    return new Date(year, endMonth, 1, 0, 0, 0);
  }

  function getCountdown() {
    var now = new Date();
    var end = getQuarterEndDate();
    var diff = end - now;

    if (diff <= 0) {
      return { days: 0, hours: 0, minutes: 0, expired: true };
    }

    return {
      days: Math.floor(diff / (1000 * 60 * 60 * 24)),
      hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
      minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
      expired: false
    };
  }

  // ===== CLAIM-WINDOW UTILITIES =====
  // Parse an ISO 'YYYY-MM-DD' as a LOCAL end-of-day Date (avoids the UTC
  // shift you get from `new Date('YYYY-MM-DD')`).
  function parseIsoEndOfDay(iso) {
    if (!iso) return null;
    var m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.trim());
    if (!m) return null;
    return new Date(+m[1], +m[2] - 1, +m[3], 23, 59, 59);
  }

  // Fallback deadline: Nth day of the current quarter's first month.
  function fallbackClaimDeadline() {
    var now = new Date();
    var qStartMonth = Math.floor(now.getMonth() / 3) * 3;
    return new Date(now.getFullYear(), qStartMonth, CONFIG.CLAIM_WINDOW_DAYS, 23, 59, 59);
  }

  // Are we currently inside the claim window for last quarter's points?
  function getClaimWindow(prevPoints, claimDeadlineIso) {
    if (!(prevPoints > 0)) return { active: false };
    var deadline = parseIsoEndOfDay(claimDeadlineIso) || fallbackClaimDeadline();
    var now = new Date();
    if (now > deadline) return { active: false };
    return { active: true, deadline: deadline };
  }

  function formatDayMonth(d) {
    return d.getDate() + ' ' + MONTH_ABBR[d.getMonth()];
  }

  // "Q2-2026" or "Q2 2026" -> "Q2 2026"; empty -> ''
  function prettyQuarter(label) {
    if (!label) return '';
    return String(label).replace('-', ' ').trim();
  }

  // ===== FORMAT POINTS =====
  function formatPoints(points) {
    return Math.round(points).toLocaleString('en-AU');
  }

  // ===== BUILD COUNTDOWN UNIT =====
  function buildCountdownUnit(value, label, id) {
    var unit = el('div', 'fb-rewards-countdown-unit');
    var num = el('span', 'fb-rewards-countdown-num', String(value));
    num.id = id;
    var lbl = el('span', 'fb-rewards-countdown-label', label);
    unit.appendChild(num);
    unit.appendChild(lbl);
    return unit;
  }

  // ===== RENDER: CLAIM WINDOW (last quarter + this quarter side by side) =====
  function renderClaimWindowBanner(container, points, prevPoints, prevLabel, deadline, capReached, claimStart) {
    var quarter = getCurrentQuarter();

    var banner = el('div', 'fb-rewards-banner fb-rewards-claim');
    banner.id = 'fb-rewards-active';

    // --- Column 1: LAST QUARTER (claimable) ---
    var prevCol = el('div', 'fb-rewards-col fb-rewards-col-prev');
    var prevTag = el('div', 'fb-rewards-col-tag');
    prevTag.appendChild(document.createTextNode('Last quarter'));
    if (prettyQuarter(prevLabel)) {
      var prevQ = el('span', 'fb-rewards-col-tag-q', prettyQuarter(prevLabel));
      prevTag.appendChild(document.createTextNode(' · '));
      prevTag.appendChild(prevQ);
    }
    prevCol.appendChild(prevTag);

    var prevMain = el('div', 'fb-rewards-col-main');
    prevMain.appendChild(el('span', 'fb-rewards-points-number prev', formatPoints(prevPoints)));
    prevMain.appendChild(el('span', 'fb-rewards-points-label', 'points to claim'));
    prevCol.appendChild(prevMain);

    // Redemption-period note (restored at Luke's request 2026-07-03 for the
    // 8-17 July Q2 window). Reads claim_window_start + claim_deadline metafields;
    // without a start date it degrades to "Redeem by <deadline>".
    var startDate = parseIsoEndOfDay(claimStart);
    var noteText = startDate
      ? 'Redeem ' + formatDayMonth(startDate) + ' – ' + formatDayMonth(deadline)
      : 'Redeem by ' + formatDayMonth(deadline);
    prevCol.appendChild(el('div', 'fb-rewards-claim-note', noteText));
    banner.appendChild(prevCol);

    banner.appendChild(el('div', 'fb-rewards-divider'));

    // --- Column 2: THIS QUARTER (accruing) ---
    var curCol = el('div', 'fb-rewards-col fb-rewards-col-current');
    var curTag = el('div', 'fb-rewards-col-tag');
    curTag.appendChild(document.createTextNode('This quarter'));
    var curQ = el('span', 'fb-rewards-col-tag-q', quarter.display);
    curTag.appendChild(document.createTextNode(' · '));
    curTag.appendChild(curQ);
    curCol.appendChild(curTag);

    var curMain = el('div', 'fb-rewards-col-main');
    curMain.appendChild(el('span', capReached ? 'fb-rewards-points-number cap' : 'fb-rewards-points-number',
      formatPoints(points)));
    curMain.appendChild(el('span', 'fb-rewards-points-label', capReached ? 'max reached' : 'points earned'));
    curCol.appendChild(curMain);

    curCol.appendChild(el('div', 'fb-rewards-col-sub', 'Earning now'));
    banner.appendChild(curCol);

    // --- CTA ---
    var cta = el('a', 'fb-rewards-cta', 'View Rewards →');
    cta.href = CONFIG.REWARDS_PAGE;
    banner.appendChild(cta);

    while (container.firstChild) container.removeChild(container.firstChild);
    container.appendChild(banner);
  }

  // ===== RENDER: LOGGED-IN CUSTOMER WITH POINTS =====
  function renderPointsBanner(container, points, capReached) {
    var quarter = getCurrentQuarter();
    var countdown = getCountdown();

    var banner = el('div', capReached ? 'fb-rewards-banner cap-reached' : 'fb-rewards-banner');
    banner.id = 'fb-rewards-active';

    // Points section
    var pointsDiv = el('div', 'fb-rewards-points');
    pointsDiv.appendChild(el('div', 'fb-rewards-points-number', formatPoints(points)));
    var pointsLabel = el('div', 'fb-rewards-points-label');
    pointsLabel.appendChild(document.createTextNode('Reward'));
    pointsLabel.appendChild(document.createElement('br'));
    pointsLabel.appendChild(document.createTextNode('Points'));
    pointsDiv.appendChild(pointsLabel);
    banner.appendChild(pointsDiv);

    // Cap badge (if at max)
    if (capReached) {
      banner.appendChild(el('span', 'fb-rewards-cap-badge', 'Max Reached'));
    }

    // Divider
    banner.appendChild(el('div', 'fb-rewards-divider'));

    // Quarter info
    var quarterDiv = el('div', 'fb-rewards-quarter');
    var strong = document.createElement('strong');
    strong.textContent = quarter.display;
    quarterDiv.appendChild(strong);
    quarterDiv.appendChild(document.createElement('br'));
    quarterDiv.appendChild(document.createTextNode(quarter.months));
    banner.appendChild(quarterDiv);

    // Divider
    banner.appendChild(el('div', 'fb-rewards-divider'));

    // Countdown
    var countdownDiv = el('div', 'fb-rewards-countdown');
    countdownDiv.appendChild(buildCountdownUnit(countdown.days, 'Days', 'fb-cd-days'));
    countdownDiv.appendChild(buildCountdownUnit(countdown.hours, 'Hours', 'fb-cd-hours'));
    countdownDiv.appendChild(buildCountdownUnit(countdown.minutes, 'Mins', 'fb-cd-mins'));
    banner.appendChild(countdownDiv);

    // CTA link
    var cta = el('a', 'fb-rewards-cta', 'View Rewards →');
    cta.href = CONFIG.REWARDS_PAGE;
    banner.appendChild(cta);

    // Clear container and append
    while (container.firstChild) {
      container.removeChild(container.firstChild);
    }
    container.appendChild(banner);

    // Start live countdown
    startCountdownUpdater();
  }

  // ===== RENDER: NOT ENROLLED / ZERO POINTS =====
  function renderPromoBanner(container) {
    var banner = el('div', 'fb-rewards-banner-promo');

    var text = el('div', 'fb-rewards-promo-text');
    text.appendChild(document.createTextNode('🏆 '));
    var strong = document.createElement('strong');
    strong.textContent = 'Firebox Rewards';
    text.appendChild(strong);
    text.appendChild(document.createTextNode(' — Earn points on every order. Redeem for gear, events & more.'));
    banner.appendChild(text);

    var cta = el('a', 'fb-rewards-promo-cta', 'Learn More');
    cta.href = CONFIG.REWARDS_PAGE;
    banner.appendChild(cta);

    while (container.firstChild) {
      container.removeChild(container.firstChild);
    }
    container.appendChild(banner);
  }

  // ===== COUNTDOWN UPDATER =====
  function startCountdownUpdater() {
    setInterval(function() {
      var countdown = getCountdown();
      var daysEl = document.getElementById('fb-cd-days');
      var hoursEl = document.getElementById('fb-cd-hours');
      var minsEl = document.getElementById('fb-cd-mins');

      if (daysEl) daysEl.textContent = countdown.days;
      if (hoursEl) hoursEl.textContent = countdown.hours;
      if (minsEl) minsEl.textContent = countdown.minutes;

      if (countdown.expired) {
        window.location.reload();
      }
    }, CONFIG.UPDATE_INTERVAL);
  }

  // ===== INITIALISE =====
  function init() {
    var container = document.getElementById('fb-rewards-container');
    if (!container) return;

    var isLoggedIn = container.dataset.loggedIn === 'true';
    var points = parseFloat(container.dataset.points || '0');
    var capReached = container.dataset.capReached === 'true';
    var isRewardsMember = container.dataset.rewardsMember === 'true';
    var prevPoints = parseFloat(container.dataset.prevPoints || '0');
    var prevLabel = container.dataset.prevLabel || '';
    var claimDeadline = container.dataset.claimDeadline || '';
    var claimStart = container.dataset.claimStart || '';

    if (!isLoggedIn || !isRewardsMember) {
      renderPromoBanner(container);
      return;
    }

    // Claim window: last quarter's points still claimable -> show both balances.
    var claim = getClaimWindow(prevPoints, claimDeadline);
    if (claim.active) {
      renderClaimWindowBanner(container, points, prevPoints, prevLabel, claim.deadline, capReached, claimStart);
      return;
    }

    if (points > 0) {
      renderPointsBanner(container, points, capReached);
    } else {
      renderPromoBanner(container);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
